import { Fragment, useContext, useMemo, useRef, useState } from "react";
import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { FaFileExcel } from "react-icons/fa";
import { MdExpandMore, MdOutlineDelete } from "react-icons/md";
import { motion, useReducedMotion } from "framer-motion";
import ImportCourseImage from "./ImportCourseImage";
import ImportFolderSelect from "./ImportFolderSelect";
import { DarkModeContext } from "../Context/DarkModeContext";
import axiosInstance from "../Context/axiosInstance";
import { getActionError } from "../utils/actionFeedback";
import {
  changeImportDestination,
  flattenImportFolders,
  importFields,
  importRowErrors,
  inferColumns,
  mapImportRows,
  readImportSheet,
  resolveImportRow,
  suggestImportNames,
} from "../utils/courseImport";

const loadExcel = async () => {
  const module = await import("xlsx");
  return module.read ? module : module.default;
};
const destinationFields = [
  ["courseGrade", "Academic level", "levelName"],
  ["courseStore", "Store", "storeName"],
  ["courseFolder", "Folder", "folderName"],
];
const inputClass =
  "w-full min-w-0 rounded-lg border border-slate-400/40 bg-white p-2 text-sm text-slate-900 focus:ring-2 focus:ring-blue-500";
const buttonClass =
  "rounded-xl border border-slate-400/40 px-4 py-2 text-sm font-semibold disabled:opacity-40";
const primaryClass = `${buttonClass} bg-emerald-700 text-white hover:bg-emerald-800`;
const reviewLayoutClass = [
  "w-full min-w-0 rounded-2xl border border-slate-400/25 shadow-sm",
  "[&_td]:min-w-0 [&_th]:min-w-0 [&_td]:[overflow-wrap:anywhere] [&_th]:[overflow-wrap:anywhere]",
  "[&_input]:max-w-full [&_select]:max-w-full [&_tr[data-details]>td]:p-0",
  "[@media(max-width:1199px)]:[&_table]:block [@media(max-width:1199px)]:[&_table]:w-full",
  "[@media(max-width:1199px)]:[&_tbody]:block [@media(max-width:1199px)]:[&_tbody]:w-full",
  "[@media(max-width:1199px)]:[&_thead]:hidden [@media(max-width:1199px)]:[&_colgroup]:hidden",
  "[@media(max-width:1199px)]:[&_tbody_tr]:grid [@media(max-width:1199px)]:[&_tbody_tr]:grid-cols-2",
  "[@media(max-width:1199px)]:[&_tbody_tr]:gap-x-3 [@media(max-width:1199px)]:[&_tbody_tr]:p-3",
  "[@media(max-width:1199px)]:[&_td]:block [@media(max-width:1199px)]:[&_td]:order-2",
  "[@media(max-width:1199px)]:[&_td:first-child]:order-none [@media(max-width:1199px)]:[&_td:first-child]:text-left",
  "[@media(max-width:1199px)]:[&_td:last-child]:order-1 [@media(max-width:1199px)]:[&_td:last-child]:text-right",
  "[@media(max-width:1199px)]:[&_td[data-label]]:before:content-[attr(data-label)]",
  "[@media(max-width:1199px)]:[&_td[data-label]]:before:block [@media(max-width:1199px)]:[&_td[data-label]]:before:mb-2",
  "[@media(max-width:1199px)]:[&_td[data-label]]:before:text-xs [@media(max-width:1199px)]:[&_td[data-label]]:before:font-semibold",
  "[@media(max-width:1199px)]:[&_td[data-label]]:before:text-slate-500",
  "[@media(max-width:1199px)]:[&_tbody_tr[data-details]]:block [@media(max-width:1199px)]:[&_tbody_tr[data-details]]:p-0",
  "[@media(max-width:479px)]:[&_td[data-label]]:col-span-full",
].join(" ");

export default function CourseImportWizard({
  defaults,
  disabled,
  onCreate,
  progress,
}) {
  const { isDarkMode } = useContext(DarkModeContext);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [book, setBook] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [sheetData, setSheetData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [page, setPage] = useState(1);
  const [bulk, setBulk] = useState({
    courseGrade: "",
    courseStore: "",
    courseFolder: "",
  });
  const [freeConfirmed, setFreeConfirmed] = useState(false);
  const [notice, setNotice] = useState("");
  const [expandedDetails, setExpandedDetails] = useState({});
  const reduceMotion = useReducedMotion();
  const operation = useRef(0);
  const catalogOperation = useRef(0);
  const saveLock = useRef(false);
  const dialogPanel = useRef(null);
  const optionsFor = (key, storeId) =>
    !catalog
      ? []
      : key === "courseGrade"
        ? catalog.levels
        : key === "courseStore"
          ? catalog.stores
          : catalog.folders[String(storeId)] || [];
  const nameFor = (key, id, storeId) => {
    const option = optionsFor(key, storeId).find(
      (item) => String(item.id) === String(id),
    );
    return option?.path || option?.name || "Unresolved";
  };
  const checked = useMemo(
    () =>
      catalog
        ? rows.map((row) => ({ row, errors: importRowErrors(row, catalog) }))
        : [],
    [rows, catalog],
  );
  const issueCount = checked.filter(
    ({ errors }) => Object.keys(errors).length,
  ).length;
  const filtered = checked.filter(
    ({ errors }) => !onlyIssues || Object.keys(errors).length,
  );
  const pages = Math.max(1, Math.ceil(filtered.length / 25));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * 25, currentPage * 25);
  const freeCount = rows.filter(
    (row) =>
      Number(row.coursePrice) === 0 ||
      Number(row.discountPercentage) === 100 ||
      Number(row.courseDiscountAmount) === Number(row.coursePrice),
  ).length;

  const loadCatalog = async () => {
    const request = ++catalogOperation.current;
    setLoadingCatalog(true);
    setCatalogError("");
    setCatalog(null);
    try {
      const results = await Promise.allSettled([
        axiosInstance.get("/academic-levels"),
        axiosInstance.get(
          "/stores?page=1&pageSize=100&sortColumn=Name&sortDirection=asc&search=",
        ),
      ]);
      const failure = results.find((result) => result.status === "rejected");
      if (failure) throw failure.reason;
      const levels = results[0].value.data;
      let stores = results[1].value.data.response || [];
      const totalPages = Math.ceil(
        (results[1].value.data.totalCount || stores.length) / 100,
      );
      for (let next = 2; next <= totalPages; next += 1) {
        if (request !== catalogOperation.current) return;
        const { data } = await axiosInstance.get(
          `/stores?page=${next}&pageSize=100&sortColumn=Name&sortDirection=asc&search=`,
        );
        stores = [...stores, ...(data.response || [])];
      }
      const folders = {};
      // Limit concurrent requests when there are many stores.
      for (let offset = 0; offset < stores.length; offset += 8) {
        if (request !== catalogOperation.current) return;
        const batch = stores.slice(offset, offset + 8);
        const trees = await Promise.allSettled(
          batch.map((store) =>
            axiosInstance.get(`/folders/tree?storeId=${store.id}`),
          ),
        );
        const failed = trees.findIndex(
          (result) => result.status === "rejected",
        );
        if (failed >= 0)
          throw new Error(
            `Could not load folders for ${batch[failed].name}. Please retry.`,
          );
        trees.forEach((result, index) => {
          folders[String(batch[index].id)] = flattenImportFolders(
            result.value.data || [],
          );
        });
      }
      if (request === catalogOperation.current)
        setCatalog({ levels, stores, folders });
    } catch (err) {
      if (request === catalogOperation.current)
        setCatalogError(
          getActionError(
            err,
            err.message || "Could not load destinations. Please retry.",
          ),
        );
    } finally {
      if (request === catalogOperation.current) setLoadingCatalog(false);
    }
  };
  const close = () => {
    if (saveLock.current) return;
    operation.current += 1;
    catalogOperation.current += 1;
    setOpen(false);
  };
  const start = () => {
    operation.current += 1;
    setExpandedDetails({});
    setStep(1);
    setBook(null);
    setSheetData(null);
    setSheetName("");
    setMapping({});
    setRows([]);
    setCatalog(null);
    setError("");
    setNotice("");
    setReading(false);
    setSelected([]);
    setOnlyIssues(false);
    setPage(1);
    setFreeConfirmed(false);
    setBulk({ courseGrade: "", courseStore: "", courseFolder: "" });
    setOpen(true);
    loadCatalog();
  };
  const chooseSheet = async (workbook, name, request) => {
    const XLSX = await loadExcel();
    const data = readImportSheet(XLSX, workbook.Sheets[name]);
    if (request !== operation.current) return;
    setSheetData(data);
    setMapping(inferColumns(data.headers));
  };
  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const request = ++operation.current;
    setError("");
    setBook(null);
    setSheetData(null);
    setSheetName("");
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setError("Choose an .xlsx, .xls, or .csv file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Choose a file smaller than 10 MB.");
      return;
    }
    setReading(true);
    try {
      const XLSX = await loadExcel();
      const workbook = XLSX.read(await file.arrayBuffer(), {
        type: "array",
        sheetRows: 1002,
      });
      if (request !== operation.current) return;
      if (!workbook.SheetNames.length)
        throw new Error("The workbook has no worksheets.");
      setBook(workbook);
      setSheetName(workbook.SheetNames[0]);
      await chooseSheet(workbook, workbook.SheetNames[0], request);
    } catch (err) {
      if (request === operation.current)
        setError(err.message || "Could not read the spreadsheet.");
    } finally {
      if (request === operation.current) setReading(false);
    }
  };
  const template = async () => {
    try {
      const XLSX = await loadExcel();
      const workbook = XLSX.utils.book_new();
      const sheet = XLSX.utils.aoa_to_sheet([
        importFields.map(([, label]) => label),
      ]);
      sheet["!cols"] = importFields.map(() => ({ wch: 28 }));
      XLSX.utils.book_append_sheet(workbook, sheet, "Courses");
      XLSX.writeFile(workbook, "courses-import-template.xlsx");
    } catch {
      setError("Could not download the template. Please retry.");
    }
  };
  const review = async () => {
    if (!sheetData || !catalog || reading) return;
    const request = ++operation.current;
    setReading(true);
    setError("");
    try {
      const XLSX = await loadExcel();
      const imported = mapImportRows(XLSX, sheetData, mapping);
      if (!imported.length)
        throw new Error("No courses found in the mapped columns.");
      if (request !== operation.current) return;
      setRows(imported.map((row) => resolveImportRow(row, catalog, defaults)));
      setExpandedDetails({});
      setStep(2);
      setSelected([]);
      setPage(1);
      setNotice("");
    } catch (err) {
      if (request === operation.current) setError(err.message);
    } finally {
      if (request === operation.current) setReading(false);
    }
  };
  const update = (rowNumber, key, value) => {
    setRows((items) =>
      items.map((row) =>
        row.rowNumber === rowNumber
          ? destinationFields.some(([field]) => field === key)
            ? changeImportDestination(row, key, value, catalog)
            : { ...row, [key]: value }
          : row,
      ),
    );
    setFreeConfirmed(false);
    setNotice("");
  };
  const applyBulk = () => {
    setRows((items) =>
      items.map((row) => {
        if (!selected.includes(row.rowNumber)) return row;
        let next = row;
        for (const [key] of destinationFields)
          if (bulk[key])
            next = changeImportDestination(next, key, bulk[key], catalog);
        return next;
      }),
    );
    setNotice(
      `Destination changes applied to ${selected.length} selected courses.`,
    );
    setSelected([]);
    setBulk({ courseGrade: "", courseStore: "", courseFolder: "" });
  };
  const create = async () => {
    if (
      saveLock.current ||
      issueCount ||
      !rows.length ||
      (freeCount && !freeConfirmed)
    )
      return;
    saveLock.current = true;
    setSaving(true);
    setError("");
    try {
      await onCreate(rows);
      setOpen(false);
    } catch (err) {
      setError(
        getActionError(
          err,
          err.message ||
            "Could not create courses. Your import is preserved; please retry.",
        ),
      );
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };
  const select = (key, value, storeId, onChange, label) =>
    key === "courseFolder" ? (
      <ImportFolderSelect
        folders={optionsFor(key, storeId)}
        value={value}
        onChange={onChange}
        disabled={!storeId}
        label={label}
        centered={step === 2}
        isDarkMode={isDarkMode}
        portalTarget={dialogPanel.current}
      />
    ) : (
      <select
        aria-label={label}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className={inputClass}
      >
        <option value="">
          {key === "courseFolder" && !storeId
            ? "Choose a store first"
            : "Choose…"}
        </option>
        {optionsFor(key, storeId).map((option) => (
          <option key={option.id} value={option.id}>
            {option.path || option.name}
          </option>
        ))}
      </select>
    );

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={start}
        className={`${primaryClass} flex items-center gap-2`}
      >
        <FaFileExcel aria-hidden="true" />
        Import from Excel
      </button>
      <Dialog open={open} onClose={close} className="relative z-[70]">
        <div
          className="fixed inset-0 bg-slate-950/65 backdrop-blur-sm"
          aria-hidden="true"
        />
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6">
          <DialogPanel
            ref={dialogPanel}
            className={`flex max-h-[calc(100dvh-2rem)] w-full max-w-[1440px] flex-col overflow-hidden rounded-2xl border shadow-2xl ${isDarkMode ? "border-slate-700 bg-[#191919] text-white" : "border-slate-200 bg-white text-slate-900"}`}
          >
            <div className="border-b border-slate-400/25 p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3">
                <DialogTitle className="text-xl font-bold">
                  Import courses
                </DialogTitle>
                <button
                  type="button"
                  onClick={close}
                  disabled={saving}
                  className={buttonClass}
                >
                  Cancel
                </button>
              </div>
              <Description className="mt-2 text-sm text-slate-500">
                Upload a spreadsheet, review destinations, then create your
                courses.
              </Description>
              <ol
                aria-label="Import steps"
                className="mt-4 flex flex-wrap gap-4 text-sm"
              >
                {[
                  "Upload & map columns",
                  "Review & fix",
                  "Confirm & create",
                ].map((label, index) => (
                  <li
                    key={label}
                    aria-current={step === index + 1 ? "step" : undefined}
                    className={
                      step === index + 1
                        ? "font-bold text-emerald-600"
                        : "text-slate-500"
                    }
                  >
                    {index + 1}. {label}
                  </li>
                ))}
              </ol>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
              {error && (
                <p
                  role="alert"
                  className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700"
                >
                  {error}
                </p>
              )}
              {step === 1 && (
                <div className="space-y-5">
                  <div className="rounded-xl border border-slate-400/30 p-4 text-sm">
                    <p>
                      Use course names, prices, and destination names—no IDs
                      needed. Put the parent folder in Folder and a subfolder
                      inside it in Subfolder (for example, Science and Physics).
                      Subfolder names are matched within that folder in the
                      selected store. Nested subfolders are included at every
                      depth. If that parent and child pair repeats, use the full
                      parent path in Folder. You can also put a full path such
                      as Science / Physics in Folder.
                    </p>
                    <p className="mt-2">
                      Blank destinations use your current course form selections
                      when available. You can also assign them together in the
                      next step.
                    </p>

                    <button
                      type="button"
                      onClick={template}
                      className="mt-3 font-semibold text-blue-500 underline"
                    >
                      Download Excel template
                    </button>
                  </div>
                  {rows.length > 0 && (
                    <p className="text-sm text-amber-600">
                      Reviewing the spreadsheet again will replace your previous
                      review edits and removed rows.
                    </p>
                  )}
                  {loadingCatalog && (
                    <p role="status">
                      Loading stores, academic levels, and folders…
                    </p>
                  )}
                  {catalogError && (
                    <p role="alert" className="text-red-500">
                      {catalogError}{" "}
                      <button
                        type="button"
                        onClick={loadCatalog}
                        className="underline"
                      >
                        Retry destinations
                      </button>
                    </p>
                  )}
                  <label className="block text-sm font-semibold">
                    Spreadsheet file
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={chooseFile}
                      disabled={reading}
                      className={`${inputClass} mt-2`}
                    />
                  </label>
                  {book && (
                    <label className="block text-sm font-semibold">
                      Worksheet
                      <select
                        className={`${inputClass} mt-2`}
                        value={sheetName}
                        disabled={reading}
                        onChange={async (event) => {
                          const name = event.target.value,
                            request = ++operation.current;
                          setSheetName(name);
                          setSheetData(null);
                          setError("");
                          setReading(true);
                          try {
                            await chooseSheet(book, name, request);
                          } catch (err) {
                            if (request === operation.current)
                              setError(err.message);
                          } finally {
                            if (request === operation.current)
                              setReading(false);
                          }
                        }}
                      >
                        {book.SheetNames.map((name) => (
                          <option key={name}>{name}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {reading && <p role="status">Reading spreadsheet…</p>}
                  {sheetData && (
                    <div>
                      <h3 className="font-semibold">Match your columns</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Check the detected columns. Name and Price are required;
                        other fields can be left unmapped. Fill only one
                        discount column per course: percentage or amount, never
                        both.
                      </p>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {importFields.map(([key, label, , required]) => (
                          <label key={key} className="text-sm">
                            {label}
                            {required ? " *" : ""}
                            <select
                              className={`${inputClass} mt-1`}
                              value={mapping[key] ?? -1}
                              onChange={(event) =>
                                setMapping((current) => ({
                                  ...current,
                                  [key]: Number(event.target.value),
                                }))
                              }
                            >
                              <option value={-1}>Not mapped</option>
                              {sheetData.headers.map((header, index) => (
                                <option key={index} value={index}>
                                  {header || "Unnamed column"} (column{" "}
                                  {index + 1})
                                </option>
                              ))}
                            </select>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {step === 2 && (
                <div className="space-y-5 [&_input:not([type=checkbox])]:min-h-[44px] [&_input:not([type=checkbox])]:text-center [&_select]:min-h-[44px] [&_select]:text-center [&_input[type=checkbox]]:h-4 [&_input[type=checkbox]]:w-4 [&_input[type=checkbox]]:accent-emerald-600">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p role="status" className="font-semibold">
                      {rows.length - issueCount} ready · {issueCount} need
                      attention
                    </p>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={onlyIssues}
                        onChange={(event) => {
                          setOnlyIssues(event.target.checked);
                          setPage(1);
                        }}
                      />
                      Show only issues
                    </label>
                  </div>
                  <p className="text-sm text-slate-500">
                    Review the matched destinations. Correct a repeated name
                    once, then use “Apply to same value” to update every
                    matching row. Folder changes apply within the same store.
                  </p>
                  <div
                    className={`rounded-2xl border p-5 ${isDarkMode ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50/80"}`}
                  >
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      <strong className="text-sm">
                        {selected.length} selected
                      </strong>
                      <button
                        type="button"
                        onClick={() =>
                          setSelected(filtered.map(({ row }) => row.rowNumber))
                        }
                        className="text-sm text-blue-500 underline"
                      >
                        Select all {filtered.length} shown by filter
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelected([])}
                        className="text-sm text-blue-500 underline"
                      >
                        Clear selection
                      </button>
                      <button
                        type="button"
                        disabled={!selected.length}
                        onClick={() => {
                          setRows((items) =>
                            items.filter(
                              (row) => !selected.includes(row.rowNumber),
                            ),
                          );
                          setNotice(
                            `Removed ${selected.length} courses from this import.`,
                          );
                          setSelected([]);
                        }}
                        className="ml-auto text-sm text-red-500 disabled:opacity-40"
                      >
                        Remove selected
                      </button>
                    </div>
                    <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-3">
                      {destinationFields.map(([key, label]) => (
                        <label
                          key={key}
                          className="space-y-2 text-center text-sm font-medium"
                        >
                          {label}
                          {select(
                            key,
                            bulk[key],
                            bulk.courseStore,
                            (value) =>
                              setBulk((current) => ({
                                ...current,
                                [key]: value,
                                ...(key === "courseStore"
                                  ? { courseFolder: "" }
                                  : {}),
                              })),
                            `Bulk ${label}`,
                          )}
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={applyBulk}
                      disabled={
                        !selected.length || !Object.values(bulk).some(Boolean)
                      }
                      className={`${buttonClass} mt-3`}
                    >
                      Apply destination to selected
                    </button>
                    <p className="mt-2 text-xs text-slate-500">
                      Only chosen fields change. Changing a store rechecks each
                      course’s original folder name.
                    </p>
                  </div>
                  {notice && (
                    <p role="status" className="text-sm text-emerald-600">
                      {notice}
                    </p>
                  )}
                  <div className={`${reviewLayoutClass} overflow-hidden`}>
                    <table className="w-full table-fixed text-center text-sm [&_td]:align-middle [&_td]:px-2 [&_td]:py-4 [&_input]:mx-auto [&_select]:mx-auto">
                      <caption className="sr-only">
                        Imported courses and destination review
                      </caption>
                      <colgroup>
                        {[4, 19, 9, 16, 13, 27, 8, 4].map((width, index) => (
                          <col key={index} style={{ width: `${width}%` }} />
                        ))}
                      </colgroup>
                      <thead
                        className={isDarkMode ? "bg-slate-800" : "bg-slate-50"}
                      >
                        <tr>
                          {[
                            "Select",
                            "Course",
                            "Price",
                            "Academic level",
                            "Store",
                            "Folder",
                            "Status",
                          ].map((label) => (
                            <th
                              key={label}
                              scope="col"
                              className="px-2 py-4 text-xs font-semibold text-slate-500"
                            >
                              {label}
                            </th>
                          ))}
                          <th scope="col" className="w-12 p-3">
                            <span className="sr-only">Remove</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map(({ row, errors }) => (
                          <Fragment key={row.rowNumber}>
                            <tr
                              className={`border-t border-slate-400/20 transition-colors ${selected.includes(row.rowNumber) ? (isDarkMode ? "bg-emerald-900/20" : "bg-emerald-50/60") : isDarkMode ? "hover:bg-slate-800/40" : "hover:bg-slate-50/70"}`}
                            >
                              <td className="p-3">
                                <input
                                  aria-label={`Select row ${row.rowNumber}`}
                                  type="checkbox"
                                  checked={selected.includes(row.rowNumber)}
                                  onChange={(event) =>
                                    setSelected((current) =>
                                      event.target.checked
                                        ? [...current, row.rowNumber]
                                        : current.filter(
                                            (id) => id !== row.rowNumber,
                                          ),
                                    )
                                  }
                                />
                              </td>
                              <td data-label="Course" className="p-3">
                                <span className="mt-3 mb-2 block text-xs font-medium text-slate-400">
                                  Row {row.rowNumber}
                                </span>
                                <input
                                  aria-label={`Name row ${row.rowNumber}`}
                                  className={inputClass}
                                  value={row.courseName}
                                  onChange={(event) =>
                                    update(
                                      row.rowNumber,
                                      "courseName",
                                      event.target.value,
                                    )
                                  }
                                />
                                <button
                                  type="button"
                                  aria-expanded={
                                    !!expandedDetails[row.rowNumber]
                                  }
                                  aria-controls={`course-details-${row.rowNumber}`}
                                  onClick={() =>
                                    setExpandedDetails((current) => ({
                                      ...current,
                                      [row.rowNumber]: !current[row.rowNumber],
                                    }))
                                  }
                                  className="mx-auto mt-2 inline-flex items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-blue-500 hover:bg-blue-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                >
                                  Discounts & links
                                  <MdExpandMore
                                    aria-hidden="true"
                                    className={`text-base transition-transform motion-reduce:transition-none ${expandedDetails[row.rowNumber] ? "rotate-180" : ""}`}
                                  />
                                </button>
                              </td>
                              <td data-label="Price" className="p-3">
                                <input
                                  aria-label={`Price row ${row.rowNumber}`}
                                  className={inputClass}
                                  value={row.coursePrice}
                                  onChange={(event) =>
                                    update(
                                      row.rowNumber,
                                      "coursePrice",
                                      event.target.value,
                                    )
                                  }
                                />
                              </td>
                              {destinationFields.map(
                                ([key, label, sourceKey]) => (
                                  <td
                                    key={key}
                                    data-label={label}
                                    className="p-3"
                                  >
                                    {/* <p className="mb-2 truncate text-center text-xs text-slate-400">
                                      From file: {row[sourceKey] || "(blank)"}
                                    </p> */}
                                    {select(
                                      key,
                                      key === "courseFolder"
                                        ? row.courseSubfolder ||
                                            row.courseFolder
                                        : row[key],
                                      row.courseStore,
                                      (value) =>
                                        update(row.rowNumber, key, value),
                                      `${label} row ${row.rowNumber}`,
                                    )}
                                    {errors[key] && (
                                      <p className="mt-1 text-xs text-red-500">
                                        {errors[key]}
                                      </p>
                                    )}
                                    {errors[key] &&
                                      suggestImportNames(
                                        row[sourceKey],
                                        optionsFor(key, row.courseStore),
                                      ).map((option) => (
                                        <button
                                          key={option.id}
                                          type="button"
                                          className="mx-auto mt-2 block text-center text-xs text-blue-500 underline"
                                          onClick={() =>
                                            update(
                                              row.rowNumber,
                                              key,
                                              String(option.id),
                                            )
                                          }
                                        >
                                          Use suggested:{" "}
                                          {option.path || option.name}
                                        </button>
                                      ))}
                                  </td>
                                ),
                              )}
                              <td data-label="Status" className="p-3">
                                <span
                                  className={`inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold ${Object.keys(errors).length ? "bg-amber-500/10 text-amber-600" : "bg-emerald-500/10 text-emerald-600"}`}
                                >
                                  {Object.keys(errors).length
                                    ? "Needs attention"
                                    : "Ready"}
                                </span>
                                {Object.entries(errors)
                                  .filter(
                                    ([key]) =>
                                      !destinationFields.some(
                                        ([field]) => key === field,
                                      ),
                                  )
                                  .map(([key, message]) => (
                                    <p
                                      key={key}
                                      className="mt-1 text-xs text-red-500"
                                    >
                                      {message}
                                    </p>
                                  ))}
                              </td>
                              <td className="p-3 text-center">
                                <button
                                  type="button"
                                  aria-label={`Remove row ${row.rowNumber}`}
                                  title="Remove course from import"
                                  onClick={() => {
                                    setRows((items) =>
                                      items.filter(
                                        (item) =>
                                          item.rowNumber !== row.rowNumber,
                                      ),
                                    );
                                    setSelected((items) =>
                                      items.filter(
                                        (id) => id !== row.rowNumber,
                                      ),
                                    );
                                  }}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-500 transition-colors hover:bg-red-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                                >
                                  <MdOutlineDelete
                                    size={20}
                                    aria-hidden="true"
                                  />
                                </button>
                              </td>
                            </tr>
                            <tr data-details>
                              <td colSpan={8}>
                                <motion.div
                                  id={`course-details-${row.rowNumber}`}
                                  initial={false}
                                  animate={{
                                    height: expandedDetails[row.rowNumber]
                                      ? "auto"
                                      : 0,
                                    opacity: expandedDetails[row.rowNumber]
                                      ? 1
                                      : 0,
                                  }}
                                  transition={{
                                    duration: reduceMotion ? 0 : 0.22,
                                    ease: "easeInOut",
                                  }}
                                  className="overflow-hidden"
                                  aria-hidden={!expandedDetails[row.rowNumber]}
                                  inert={
                                    expandedDetails[row.rowNumber]
                                      ? undefined
                                      : ""
                                  }
                                >
                                  <div
                                    className={`m-3 mt-0 rounded-xl border border-slate-400/20 p-4 sm:p-5 ${isDarkMode ? "bg-slate-800/40" : "bg-slate-50"}`}
                                  >
                                    <p className="mb-4 text-center text-sm font-semibold">
                                      Discounts & links ?{" "}
                                      {row.courseName || `Row ${row.rowNumber}`}
                                    </p>
                                    <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                      {importFields
                                        .slice(5)
                                        .map(([key, label]) =>
                                          key === "courseImageUrl" ? (
                                            <ImportCourseImage
                                              key={key}
                                              rowNumber={row.rowNumber}
                                              file={row.courseImageFile}
                                              url={row.courseImageUrl}
                                              error={errors.courseImageUrl}
                                              onChange={(image) =>
                                                setRows((items) =>
                                                  items.map((item) =>
                                                    item.rowNumber ===
                                                    row.rowNumber
                                                      ? { ...item, ...image }
                                                      : item,
                                                  ),
                                                )
                                              }
                                            />
                                          ) : (
                                            <label
                                              key={key}
                                              className="block min-w-0 text-center text-sm font-medium"
                                            >
                                              <span className="mb-2 block">
                                                {label}
                                              </span>
                                              <input
                                                type={
                                                  key
                                                    .toLowerCase()
                                                    .endsWith("url")
                                                    ? "url"
                                                    : "number"
                                                }
                                                min={
                                                  key
                                                    .toLowerCase()
                                                    .endsWith("url")
                                                    ? undefined
                                                    : 0
                                                }
                                                max={
                                                  key === "discountPercentage"
                                                    ? 100
                                                    : undefined
                                                }
                                                step="any"
                                                className={inputClass}
                                                value={row[key]}
                                                onChange={(event) =>
                                                  update(
                                                    row.rowNumber,
                                                    key,
                                                    event.target.value,
                                                  )
                                                }
                                                aria-invalid={!!errors[key]}
                                              />
                                              {errors[key] && (
                                                <span className="mt-2 block text-xs text-red-500">
                                                  {errors[key]}
                                                </span>
                                              )}
                                            </label>
                                          ),
                                        )}
                                    </div>
                                  </div>
                                </motion.div>
                              </td>
                            </tr>
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!filtered.length && (
                    <p className="text-sm">
                      {rows.length
                        ? "No issues remain. Turn off the filter to review all courses."
                        : "No courses remain. Go back to upload a spreadsheet."}
                    </p>
                  )}
                  <div className="flex items-center justify-end gap-3 text-sm">
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setPage(currentPage - 1)}
                      className={buttonClass}
                    >
                      Previous
                    </button>
                    Page {currentPage} of {pages}
                    <button
                      type="button"
                      disabled={currentPage === pages}
                      onClick={() => setPage(currentPage + 1)}
                      className={buttonClass}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold">
                    Create {rows.length}{" "}
                    {rows.length === 1 ? "course" : "courses"} across{" "}
                    {new Set(rows.map((row) => String(row.courseStore))).size}{" "}
                    stores and{" "}
                    {
                      new Set(
                        rows.map(
                          (row) =>
                            `${row.courseStore}:${row.courseSubfolder || row.courseFolder}`,
                        ),
                      ).size
                    }{" "}
                    folders
                  </h3>
                  <p className="text-sm text-slate-500">
                    These courses will be created when you confirm. Any other
                    unsaved courses in the form stay separate.
                  </p>
                  <ul className="space-y-2">
                    {Object.values(
                      rows.reduce((groups, row) => {
                        const key = `${row.courseGrade}:${row.courseStore}:${row.courseSubfolder || row.courseFolder}`;
                        groups[key] ||= { row, count: 0 };
                        groups[key].count += 1;
                        return groups;
                      }, {}),
                    ).map(({ row, count }) => (
                      <li
                        key={`${row.courseGrade}:${row.courseStore}:${row.courseSubfolder || row.courseFolder}`}
                        className="rounded-xl border border-slate-400/30 p-3 text-sm"
                      >
                        <strong>
                          {count} {count === 1 ? "course" : "courses"}
                        </strong>{" "}
                        · {nameFor("courseGrade", row.courseGrade)} ·{" "}
                        {nameFor("courseStore", row.courseStore)} ·{" "}
                        {nameFor(
                          "courseFolder",
                          row.courseSubfolder || row.courseFolder,
                          row.courseStore,
                        )}
                      </li>
                    ))}
                  </ul>
                  {freeCount > 0 && (
                    <label className="flex items-start gap-3 rounded-xl border border-amber-500/40 p-4 text-sm">
                      <input
                        type="checkbox"
                        checked={freeConfirmed}
                        disabled={saving}
                        onChange={(event) =>
                          setFreeConfirmed(event.target.checked)
                        }
                      />
                      I understand that {freeCount}{" "}
                      {freeCount === 1 ? "course" : "courses"} will be free,
                      based on their price or discount.
                    </label>
                  )}
                  {saving && (
                    <p role="status">{progress || "Creating courses…"}</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-400/25 p-5">
              {step > 1 ? (
                <button
                  type="button"
                  className={buttonClass}
                  disabled={saving}
                  onClick={() => {
                    setStep(step - 1);
                    setError("");
                  }}
                >
                  Back
                </button>
              ) : (
                <span className="text-xs text-slate-500">
                  Excel or CSV · Up to 1,000 rows · 10 MB maximum
                </span>
              )}
              {step === 1 && (
                <button
                  type="button"
                  onClick={review}
                  disabled={!sheetData || !catalog || reading || loadingCatalog}
                  className={primaryClass}
                >
                  Review import
                </button>
              )}
              {step === 2 && (
                <div className="flex items-center gap-3">
                  {issueCount > 0 && (
                    <span className="text-sm text-amber-600">
                      Resolve or remove {issueCount} rows to continue.
                    </span>
                  )}
                  <button
                    type="button"
                    disabled={!!issueCount || !rows.length}
                    onClick={() => {
                      setStep(3);
                      setError("");
                    }}
                    className={primaryClass}
                  >
                    Continue to confirmation
                  </button>
                </div>
              )}
              {step === 3 && (
                <button
                  type="button"
                  disabled={
                    saving ||
                    !!issueCount ||
                    !rows.length ||
                    (!!freeCount && !freeConfirmed)
                  }
                  onClick={create}
                  className={primaryClass}
                >
                  {saving
                    ? "Creating…"
                    : `Create ${rows.length} ${rows.length === 1 ? "course" : "courses"}`}
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
